/**
 * Arbre « organigramme » (Main.pas L960-1340) : services en majuscules et en
 * cascade, personnes « Nom Prénom », contrats à durée déterminée libellés
 * « Nom Prénom (du JJ/MM/AAAA au JJ/MM/AAAA) » et regroupés sous un nœud
 * « Nom Prénom » quand la personne en a plusieurs. Filtre texte (Paramètres).
 * Ne montre que les contrats visibles par l'acteur (périmètre serveur).
 */
import { useMemo, useState } from 'react';
import type { ContratVisible, Referentiels } from '../api';
import { jjmmaaaa } from '../commun';

interface Noeud { cle: string; libelle: string; idContrat?: number; enfants: Noeud[]; service?: boolean }

function construire(services: Referentiels['services'], contrats: ContratVisible[], filtre: string): Noeud[] {
  const f = filtre.trim().toLowerCase();
  const parService = new Map<number, ContratVisible[]>();
  for (const c of contrats) (parService.get(c.idService) ?? parService.set(c.idService, []).get(c.idService)!).push(c);

  const personnes = (idService: number): Noeud[] => {
    const liste = (parService.get(idService) ?? []).filter((c) => !f || `${c.nom} ${c.prenom}`.toLowerCase().includes(f));
    const parPersonne = new Map<number, ContratVisible[]>();
    for (const c of liste) (parPersonne.get(c.idReferenceUtilisateur) ?? parPersonne.set(c.idReferenceUtilisateur, []).get(c.idReferenceUtilisateur)!).push(c);
    const noeuds: Noeud[] = [];
    for (const [ref, cs] of parPersonne) {
      const nom = `${cs[0]!.nom} ${cs[0]!.prenom}`;
      const feuilles: Noeud[] = cs.map((c) => ({
        cle: `c${c.id}`, idContrat: c.id, enfants: [],
        libelle: c.dernierJourContrat ? `${nom} (du ${jjmmaaaa(c.premierJourContrat)} au ${jjmmaaaa(c.dernierJourContrat)})` : nom,
      }));
      if (feuilles.length === 1) noeuds.push(feuilles[0]!);
      else noeuds.push({ cle: `p${ref}`, libelle: nom, enfants: feuilles });
    }
    return noeuds.sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
  };

  const sousServices = (parent: number): Noeud[] =>
    services.filter((s) => s.idServiceParent === parent).sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'))
      .map((s) => ({ cle: `s${s.idService}`, libelle: s.libelle.toUpperCase(), service: true, enfants: [...sousServices(s.idService), ...personnes(s.idService)] }))
      .filter((n) => n.enfants.length > 0);
  return sousServices(-1);
}

export function ArbreOrganigramme({ services, contrats, selection, surSelection }: {
  services: Referentiels['services']; contrats: ContratVisible[]; selection: number | null; surSelection: (id: number) => void;
}): JSX.Element {
  const [filtre, setFiltre] = useState('');
  const [ouverts, setOuverts] = useState<Set<string>>(() => new Set());
  const arbre = useMemo(() => construire(services, contrats, filtre), [services, contrats, filtre]);
  const contientSelection = (n: Noeud): boolean => n.idContrat === selection || n.enfants.some(contientSelection);
  const basculer = (cle: string) => setOuverts((s) => { const n = new Set(s); n.has(cle) ? n.delete(cle) : n.add(cle); return n; });

  const rendre = (n: Noeud, niveau: number): JSX.Element => {
    const ouvert = filtre.length > 0 || ouverts.has(n.cle) || contientSelection(n);
    if (n.enfants.length === 0) {
      return <li key={n.cle}><button className={`noeud personne${n.idContrat === selection ? ' selectionne' : ''}`} style={{ paddingLeft: 14 + niveau * 16 }} onClick={() => n.idContrat && surSelection(n.idContrat)}>{n.libelle}</button></li>;
    }
    return (
      <li key={n.cle}>
        <button className={`noeud${n.service ? ' service' : ' personne'}`} style={{ paddingLeft: 14 + niveau * 16 }} aria-expanded={ouvert} onClick={() => basculer(n.cle)}>
          {n.libelle}<span className="fleche">{ouvert ? '▾' : '▸'}</span>
        </button>
        {ouvert && <ul>{n.enfants.map((e) => rendre(e, niveau + 1))}</ul>}
      </li>
    );
  };

  return (
    <aside className="arbre-organigramme" aria-label="Organigramme">
      <div className="filtre-arbre"><label htmlFor="filtre-arbre">Filtre :</label><input id="filtre-arbre" value={filtre} onChange={(e) => setFiltre(e.target.value)} /></div>
      <ul>{arbre.map((n) => rendre(n, 0))}</ul>
      {arbre.length === 0 && <p className="aide" style={{ padding: 10 }}>Aucun contrat visible.</p>}
    </aside>
  );
}
